import React, { useState, useEffect, useRef } from 'react';
import { Collapse, Card } from 'antd';
import { DownOutlined, RightOutlined, LeftOutlined, RiseOutlined } from '@ant-design/icons';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

/**
 * BarChartComponent for displaying bar charts
 * 
 * @param {Object} props
 * @param {Object} props.data - The chart data
 * @param {string} props.title - The title of the accordion
 * @param {string} props.uuid - The UUID of the data object
 * @param {number} props.currentVersion - Current version of the data
 * @param {Object} props.versionInfo - Information about all versions
 * @param {Function} props.onVersionChange - Callback when version is changed
 * @param {Function} props.registerComponent - Function to register for updates
 * @param {boolean} props.stacked - Whether to stack the bars (default: false for clustered)
 */
const BarChartComponent = ({
  data,
  title = "Sales Chart",
  uuid,
  currentVersion = 1,
  versionInfo = {},
  onVersionChange,
  registerComponent,
  stacked = false
}) => {
  // State to track whether the component is expanded
  const [isExpanded, setIsExpanded] = useState(true);
  
  // Handler for version navigation
  const handleVersionChange = (newVersion) => {
    if (onVersionChange && uuid) {
      onVersionChange(uuid, newVersion);
    }
  };
  
  //////////  Component data sync from backend ?????????????????
  const [updateCounter, setUpdateCounter] = useState(0);

  // Register this component for updates if the function is provided
  useEffect(() => {
    if (registerComponent && uuid) {
      // Create a refresh function to update this component
      const refreshComponent = () => {
        console.log(`Refreshing Component: ${uuid}`);
        setUpdateCounter(prev => prev + 1);
      };
      
      // Register this component with the provided function
      const unregister = registerComponent(refreshComponent);
      
      // Unregister when the component unmounts
      return unregister;
    }
  }, [registerComponent, uuid]);
  //////////  Component data sync from backend ?????????????????


  // Extract metadata from data if available
  const chartTitle = data?.metadata?.title || title;
  const chartSubtitle = data?.metadata?.subtitle || '';
  const trendInfo = data?.metadata?.trend || null;
  const description = data?.metadata?.description || '';
  
// Better format numbers with commas (handles decimals properly)
const formatNumber = (number) => {
  // Split into integer and decimal parts
  const parts = number.toString().split('.');
  // Add commas to integer part only
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  // Rejoin with decimal
  return parts.join('.');
};

// Check if this is a currency chart based on metrics labels
const isCurrencyChart = () => {
  if (!data?.metrics) return false;
  // Check if any metric label contains ($) or dollar
  return Object.values(data.metrics).some(metric => 
    metric.label?.includes('($)') || 
    metric.label?.toLowerCase().includes('cost') ||
    metric.label?.toLowerCase().includes('dollar')
  );
};
  
// Custom tooltip
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const showCurrency = isCurrencyChart();
    
    return (
      <div style={{
        backgroundColor: '#fff',
        border: '1px solid #ccc',
        padding: '10px',
        borderRadius: '4px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <p style={{ margin: 0, fontWeight: 'bold' }}>{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ margin: 0, color: entry.color }}>
            {`${entry.name}: ${showCurrency ? '$' : ''}${formatNumber(entry.value.toFixed(showCurrency ? 4 : 0))}`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};
  
  // Check if version navigation should be displayed
  const showVersionNavigation = versionInfo && 
                               versionInfo.available_versions && 
                               versionInfo.available_versions.length > 1;
  
  return (
    <div style={{ width: '100%' }}>
      {/* Chart Subtitle */}
      {chartSubtitle && (
        <div style={{ 
          color: '#8c8c8c', 
          marginBottom: '16px',
          fontSize: '14px'
        }}>
          {chartSubtitle}
        </div>
      )}
      
      {/* Bar Chart */}
      <div style={{ width: '100%', height: '400px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data?.chartData || []}
            margin={{
              top: 5,
              right: 30,
              left: 30,
              bottom: 5,
            }}
          >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis 
                        dataKey="name" 
                        tickLine={false} 
                        axisLine={{ stroke: '#f0f0f0' }} 
                      />
                      <YAxis 
                        tickFormatter={formatNumber}
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend wrapperStyle={{ paddingTop: '10px' }} />
                      
                      {/* Generate bar colors based on the primary and secondary colors */}
                      {data?.metrics && Object.keys(data.metrics).map((key, index) => {
                        // Use custom colors from metrics or generate a variation
                        let color = data.metrics[key].color;
                        
                        // If no color specified, use the custom colors or variations
                        if (!color) {
                          const primaryColor = '#5a00d8';   // Dark purple
                          const secondaryColor = '#d1b1ff'; // Light purple
                          
                          if (index === 0) {
                            color = primaryColor;
                          } else if (index === 1) {
                            color = secondaryColor;
                          } else {
                            // For additional categories, create variations
                            // This is a simple way to create variations by mixing the colors
                            const ratio = index / (Object.keys(data.metrics).length + 1);
                            const r1 = parseInt(primaryColor.substr(1, 2), 16);
                            const g1 = parseInt(primaryColor.substr(3, 2), 16);
                            const b1 = parseInt(primaryColor.substr(5, 2), 16);
                            
                            const r2 = parseInt(secondaryColor.substr(1, 2), 16);
                            const g2 = parseInt(secondaryColor.substr(3, 2), 16);
                            const b2 = parseInt(secondaryColor.substr(5, 2), 16);
                            
                            const r = Math.round(r1 * (1 - ratio) + r2 * ratio);
                            const g = Math.round(g1 * (1 - ratio) + g2 * ratio);
                            const b = Math.round(b1 * (1 - ratio) + b2 * ratio);
                            
                            color = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
                          }
                        }
                        
                        return (
                          <Bar 
                            key={key}
                            dataKey={key} 
                            name={data.metrics[key].label || key}
                            fill={color}
                            radius={[4, 4, 0, 0]}
                            stackId={stacked ? "stack" : undefined}
                          />
                        );
                      })}
                      
                      {/* Fallback if metrics not specified */}
                      {!data?.metrics && data?.chartData && data.chartData.length > 0 && 
                        Object.keys(data.chartData[0])
                          .filter(key => key !== 'name')
                          .map((key, index) => {
                            // Use the custom colors or variations
                            let color;
                            const primaryColor = '#5a00d8';   // Dark purple
                            const secondaryColor = '#d1b1ff'; // Light purple
                            
                            if (index === 0) {
                              color = primaryColor;
                            } else if (index === 1) {
                              color = secondaryColor;
                            } else {
                              // For additional categories, create variations
                              const ratio = index / (Object.keys(data.chartData[0]).filter(k => k !== 'name').length + 1);
                              const r1 = parseInt(primaryColor.substr(1, 2), 16);
                              const g1 = parseInt(primaryColor.substr(3, 2), 16);
                              const b1 = parseInt(primaryColor.substr(5, 2), 16);
                              
                              const r2 = parseInt(secondaryColor.substr(1, 2), 16);
                              const g2 = parseInt(secondaryColor.substr(3, 2), 16);
                              const b2 = parseInt(secondaryColor.substr(5, 2), 16);
                              
                              const r = Math.round(r1 * (1 - ratio) + r2 * ratio);
                              const g = Math.round(g1 * (1 - ratio) + g2 * ratio);
                              const b = Math.round(b1 * (1 - ratio) + b2 * ratio);
                              
                              color = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
                            }
                            
                            return (
                              <Bar 
                                key={key}
                                dataKey={key} 
                                name={key}
                                fill={color}
                                radius={[4, 4, 0, 0]}
                                stackId={stacked ? "stack" : undefined}
                              />
                            );
                          })
                      }
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                
                {/* Trend and Description Footer */}
                {(trendInfo || description) && (
                  <div style={{ 
                    marginTop: '16px',
                    paddingTop: '16px',
                    borderTop: '1px solid #f0f0f0',
                    fontSize: '14px'
                  }}>
                    {trendInfo && (
                      <div style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontWeight: 'medium',
                        marginBottom: '4px'
                      }}>
                        {trendInfo}
                        <RiseOutlined style={{ fontSize: '16px' }} />
                      </div>
                    )}
                    
                    {description && (
                      <div style={{ color: '#8c8c8c' }}>
                        {description}
                      </div>
                    )}
                  </div>
                )}
              </div>
  );
};

export default BarChartComponent;